import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquare, Mail, Save, RotateCcw, Key, Home, Info, Eye } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AVAILABLE_VARIABLES = [
  { var: '{{nome_ospite}}', desc: 'Nome dell\'ospite' },
  { var: '{{codice_prenotazione}}', desc: 'Codice prenotazione (MDP-XXXXX)' },
  { var: '{{nome_camera}}', desc: 'Nome della camera/unità' },
  { var: '{{codice_ingresso}}', desc: 'Codice di accesso alla struttura' },
  { var: '{{wifi_password}}', desc: 'Password WiFi' },
  { var: '{{data_arrivo}}', desc: 'Data di check-in' },
  { var: '{{data_partenza}}', desc: 'Data di check-out' },
  { var: '{{num_ospiti}}', desc: 'Numero ospiti' },
  { var: '{{email_ospite}}', desc: 'Email ospite' },
  { var: '{{telefono_ospite}}', desc: 'Telefono ospite' },
];

export default function AdminMessageTemplates() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [unitAccessInfo, setUnitAccessInfo] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [activeLang, setActiveLang] = useState('it'); // 'it' or 'en'
  const [previewMessage, setPreviewMessage] = useState('');

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const fetchData = async () => {
    try {
      const [templatesRes, accessRes] = await Promise.all([
        axios.get(`${API}/admin/message-templates`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/unit-access-info`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setTemplates(templatesRes.data);
      setUnitAccessInfo(accessRes.data);
      
      // Set first IT template as active
      const itTemplate = templatesRes.data.find(t => t.lingua === 'it') || templatesRes.data[0];
      if (itTemplate) {
        setActiveTemplate(itTemplate);
        setActiveLang(itTemplate.lingua || 'it');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!activeTemplate) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/admin/message-templates/${activeTemplate.id}`, {
        nome: activeTemplate.nome,
        tipo: activeTemplate.tipo,
        lingua: activeTemplate.lingua || activeLang,
        oggetto: activeTemplate.oggetto,
        contenuto: activeTemplate.contenuto,
        attivo: activeTemplate.attivo
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template salvato!');
      fetchData();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const switchLanguage = (lang) => {
    setActiveLang(lang);
    const template = templates.find(t => t.lingua === lang);
    if (template) {
      setActiveTemplate(template);
      setPreviewMessage('');
    }
  };

  const handleSaveUnitAccess = async (unitId, data) => {
    try {
      await axios.put(`${API}/admin/unit-access-info/${unitId}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Info accesso salvate!');
      fetchData();
    } catch (error) {
      console.error('Error saving unit access:', error);
      toast.error('Errore nel salvataggio');
    }
  };

  const generatePreview = () => {
    if (!activeTemplate) return;
    
    let preview = activeTemplate.contenuto;
    const sampleData = {
      '{{nome_ospite}}': 'Mario Rossi',
      '{{codice_prenotazione}}': 'MDP-ABC123',
      '{{nome_camera}}': 'LA MAISONETTE 1',
      '{{codice_ingresso}}': '1508',
      '{{wifi_password}}': 'wifi2024',
      '{{data_arrivo}}': '2026-07-15',
      '{{data_partenza}}': '2026-07-18',
      '{{num_ospiti}}': '2',
      '{{email_ospite}}': 'mario.rossi@email.com',
      '{{telefono_ospite}}': '+39 333 1234567'
    };
    
    for (const [varName, value] of Object.entries(sampleData)) {
      preview = preview.replaceAll(varName, value);
    }
    
    setPreviewMessage(preview);
  };

  const insertVariable = (varName) => {
    if (!activeTemplate) return;
    
    // Insert at cursor position or at end
    const textarea = document.getElementById('template-content');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const content = activeTemplate.contenuto;
      const newContent = content.substring(0, start) + varName + content.substring(end);
      setActiveTemplate({ ...activeTemplate, contenuto: newContent });
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Template Messaggi Check-in">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Template Messaggi Check-in">
      <Tabs defaultValue="template" className="space-y-6">
        <TabsList className="bg-gray-100">
          <TabsTrigger value="template" className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-white">
            <MessageSquare className="w-4 h-4 mr-2" />
            Template Messaggio
          </TabsTrigger>
          <TabsTrigger value="access" className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-white">
            <Key className="w-4 h-4 mr-2" />
            Codici Accesso
          </TabsTrigger>
          <TabsTrigger value="variables" className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-white">
            <Info className="w-4 h-4 mr-2" />
            Variabili Disponibili
          </TabsTrigger>
        </TabsList>

        {/* Tab Template */}
        <TabsContent value="template">
          {/* Language Selector */}
          <div className="flex items-center gap-4 mb-6 p-3 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium text-gray-700">Lingua Template:</span>
            <div className="flex gap-2">
              <Button
                variant={activeLang === 'it' ? 'default' : 'outline'}
                size="sm"
                onClick={() => switchLanguage('it')}
                className={activeLang === 'it' ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                🇮🇹 Italiano
              </Button>
              <Button
                variant={activeLang === 'en' ? 'default' : 'outline'}
                size="sm"
                onClick={() => switchLanguage('en')}
                className={activeLang === 'en' ? 'bg-blue-600 hover:bg-blue-700' : ''}
              >
                🇬🇧 English
              </Button>
            </div>
            <span className="text-xs text-gray-500 ml-auto">
              La lingua viene rilevata automaticamente dal prefisso telefonico (+39 = IT)
            </span>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Editor */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-[#C5A059]" />
                    {activeLang === 'it' ? '🇮🇹 Template Italiano' : '🇬🇧 English Template'}
                  </span>
                  <Button 
                    onClick={handleSaveTemplate}
                    disabled={saving}
                    className="bg-[#C5A059] hover:bg-[#B08D4A]"
                    data-testid="save-template-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Salvataggio...' : 'Salva'}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="template-name">Nome Template</Label>
                  <Input
                    id="template-name"
                    value={activeTemplate?.nome || ''}
                    onChange={(e) => setActiveTemplate({...activeTemplate, nome: e.target.value})}
                    placeholder="Es: checkin_whatsapp"
                    data-testid="template-name"
                  />
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label htmlFor="template-content">Contenuto Messaggio</Label>
                    <span className="text-xs text-gray-500">
                      {activeTemplate?.contenuto?.length || 0} caratteri
                    </span>
                  </div>
                  <Textarea
                    id="template-content"
                    value={activeTemplate?.contenuto || ''}
                    onChange={(e) => setActiveTemplate({...activeTemplate, contenuto: e.target.value})}
                    rows={20}
                    className="font-mono text-sm"
                    placeholder="Inserisci il template del messaggio..."
                    data-testid="template-content"
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-gray-500 w-full mb-1">Inserisci variabile:</span>
                  {AVAILABLE_VARIABLES.slice(0, 5).map(v => (
                    <Button
                      key={v.var}
                      variant="outline"
                      size="sm"
                      onClick={() => insertVariable(v.var)}
                      className="text-xs"
                    >
                      {v.var}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Preview */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Eye className="w-5 h-5 text-[#C5A059]" />
                    Anteprima
                  </span>
                  <Button 
                    variant="outline"
                    onClick={generatePreview}
                    data-testid="preview-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Genera Anteprima
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {previewMessage ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-h-[600px] overflow-y-auto">
                    <p className="text-xs text-green-600 mb-2 font-medium">Anteprima messaggio WhatsApp:</p>
                    <pre className="whitespace-pre-wrap text-sm font-sans text-gray-800">
                      {previewMessage}
                    </pre>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Eye className="w-10 h-10 mx-auto mb-3 opacity-30" />
                    <p>Clicca &quot;Genera Anteprima&quot; per vedere come apparirà il messaggio</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab Codici Accesso */}
        <TabsContent value="access">
          <div className="grid md:grid-cols-2 gap-6">
            {unitAccessInfo.map((unit) => (
              <Card key={unit.unit_id}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Home className="w-5 h-5 text-[#C5A059]" />
                    {unit.unit_name || 'Unità'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor={`code-${unit.unit_id}`}>Codice Ingresso</Label>
                    <Input
                      id={`code-${unit.unit_id}`}
                      value={unit.codice_ingresso || ''}
                      onChange={(e) => {
                        const updated = unitAccessInfo.map(u => 
                          u.unit_id === unit.unit_id 
                            ? {...u, codice_ingresso: e.target.value}
                            : u
                        );
                        setUnitAccessInfo(updated);
                      }}
                      placeholder="Es: 1508"
                      data-testid={`code-${unit.unit_id}`}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor={`wifi-${unit.unit_id}`}>Password WiFi</Label>
                    <Input
                      id={`wifi-${unit.unit_id}`}
                      value={unit.wifi_password || ''}
                      onChange={(e) => {
                        const updated = unitAccessInfo.map(u => 
                          u.unit_id === unit.unit_id 
                            ? {...u, wifi_password: e.target.value}
                            : u
                        );
                        setUnitAccessInfo(updated);
                      }}
                      placeholder="Es: wifi2024"
                      data-testid={`wifi-${unit.unit_id}`}
                    />
                  </div>

                  <div>
                    <Label htmlFor={`notes-${unit.unit_id}`}>Note Specifiche</Label>
                    <Textarea
                      id={`notes-${unit.unit_id}`}
                      value={unit.note_specifiche || ''}
                      onChange={(e) => {
                        const updated = unitAccessInfo.map(u => 
                          u.unit_id === unit.unit_id 
                            ? {...u, note_specifiche: e.target.value}
                            : u
                        );
                        setUnitAccessInfo(updated);
                      }}
                      rows={3}
                      placeholder="Note aggiuntive per questa camera..."
                    />
                  </div>

                  <Button 
                    onClick={() => handleSaveUnitAccess(unit.unit_id, {
                      codice_ingresso: unit.codice_ingresso,
                      wifi_password: unit.wifi_password,
                      note_specifiche: unit.note_specifiche
                    })}
                    className="w-full bg-[#C5A059] hover:bg-[#B08D4A]"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Salva
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Tab Variabili */}
        <TabsContent value="variables">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Info className="w-5 h-5 text-[#C5A059]" />
                Variabili Disponibili
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-500 mb-4">
                Usa queste variabili nel template. Saranno sostituite automaticamente con i dati della prenotazione.
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                {AVAILABLE_VARIABLES.map((v) => (
                  <div 
                    key={v.var}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <code className="text-sm font-mono text-[#C5A059] font-bold">
                        {v.var}
                      </code>
                      <p className="text-xs text-gray-500 mt-1">{v.desc}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(v.var);
                        toast.success('Copiato!');
                      }}
                    >
                      Copia
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
